<template>
  <div class="containers">
    <SectionHeader
      :title="$t('containerGroups.containerGroups')"
      @selectProjectUuids="
        projectUuids = $event
        loadData(1)
      "
      :projectSelect="true"
    />

    <div>
      <div class="content-area">
        <SearchField
          :label="$t('meta.searchForLabel', { entity: $t('meta.containerGroup') })"
          :name="'container-group-search-input'"
          :placeholder="$t('containerGroups.searchPlaceholder')"
          v-model="searchPhrase"
          @input="searchHandler"
        />
        <ToggleButtons v-model="filter" @input="loadData(1)" :options="filterOptions" class="full-width" />
        <LoadingSpinner v-if="loading" />
        <div v-else-if="containerGroups.length > 0">
          <div v-for="container_group in containerGroups" :key="container_group.uuid">
            <div class="area-wrap">
              <div
                v-if="
                  container_group.emptying_avg_fill_level &&
                  Object.keys(container_group.emptying_avg_fill_level).length > 0
                "
                class="fill-level fill-level--group"
              >
                <template v-for="(level, fraction) in container_group.emptying_avg_fill_level">
                  <div v-if="level" :key="`${level}-${fraction}`">
                    {{ fillLevelFormat(fraction, level) }}
                  </div>
                </template>
              </div>
              <a @click="jumpOnMap(container_group.lat, container_group.lng)" class="jump-on-map">{{
                $t('containers.showOnMap')
              }}</a>
              <h3>
                <Link :to="'/container_groups/' + container_group.uuid">{{ container_group.name }}</Link>
              </h3>
              <p v-if="container_group.preformatted">
                <span class="material-icons">gps_fixed</span><span>{{ container_group.preformatted }}</span>
              </p>
              <p>
                <span class="material-icons">delete</span
                ><span>{{ container_group.container_count }} {{ $t('meta.container') }}</span>
              </p>
              <p v-if="container_group.status">
                <span class="material-icons">{{
                  container_group.status == 'disabled' ? 'visibility_off' : 'visibility'
                }}</span
                ><span>{{ $t(`containerGroups.status.${container_group.status}`) }}</span>
              </p>
              <p v-if="container_group.project">
                <span class="material-icons">all_inbox</span><span>{{ container_group.project.name }}</span>
              </p>
            </div>
          </div>
          <div v-show="pagination" class="pagination">
            <pagination
              :records="pagination.totalObjects"
              v-model="pagination.page"
              :per-page="25"
              @paginate="loadData"
              :options="{ chunk: 5, texts: { count: $t('pagination.text') } }"
            />
          </div>
        </div>
        <div v-else class="alert alert-danger">{{ $t('containerGroups.noGroups') }}</div>
      </div>

      <div v-show="!cookiesAccepted" class="map-body map-placeholder">
        <h3>{{ $t('meta.acceptCookies') }}</h3>
        <p>{{ $t('meta.acceptCookiesDesc') }}.</p>
        <p class="cta">
          <a @click="acceptCookies" class="button button-green button-small button-round"
            >{{ $t('meta.acceptCookiesCta') }} ›</a
          >
        </p>
      </div>
      <div v-show="cookiesAccepted" id="map" class="map-body"></div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import Pagination from 'vue-pagination-2'

import SearchField from '@/components/Form/Search/SearchField.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import ToggleButtons from '@/components/ToggleButtons'
import config from '@/components/config'

import containerGroupsApi from '@/api/containerGroups'
import mapIcon from '@/assets/maps_icon_pin_blue.png'
import mapUtils from '@/utils/mapbox'
import { SEARCH_INPUT_DELAY } from '@/utils/searchInputDelay'
import { mapSettings } from '@/utils/settings'

export default {
  name: 'container_groups',
  inject: ['companyUser'],
  props: {
    searchInputDelay: {
      type: Number,
      default: SEARCH_INPUT_DELAY,
    },
  },
  components: {
    LoadingSpinner,
    Pagination,
    SearchField,
    SectionHeader,
    ToggleButtons,
  },
  computed: {
    ...mapSettings(['cookiesAccepted']),
    searchHandler() {
      return debounce(
        () => this.getContainerGroups(1),
        this.searchInputDelay, // Limit search API calls to once per 400ms while typing
        { trailing: true },
      )
    },
    filterOptions() {
      return [
        { label: this.$t('containerGroups.filterLabel.all'), value: null },
        { label: this.$t('containerGroups.filterLabel.disabled'), value: 'disabled' },
        { label: this.$t('containerGroups.filterLabel.enabled'), value: 'enabled' },
      ]
    },
  },
  data: () => ({
    containerGroups: [],
    containerGroupsGeojson: {
      type: 'FeatureCollection',
      features: [],
    },
    filter: null,
    loading: true,
    map: '',
    pagination: { page: 1, totalObjects: 0 },
    projectUuids: null,
    searchPhrase: '',
  }),
  mixins: [mapUtils],
  methods: {
    async getContainerGroups(page) {
      this.loading = true

      const params = {
        page,
        company_uuid: this.companyUser.company_uuid,
        project_uuids: this.projectUuids,
        status: this.filter,
      }

      if (this.searchPhrase.length > 0) params['search_phrase'] = this.searchPhrase

      const response = await containerGroupsApi.getAll(params)

      this.containerGroups = response.containerGroups

      this.pagination.page = page
      this.pagination.totalObjects = response.pagination?.total_objects

      this.loading = false
    },
    initMap() {
      var coordinates = []
      if (this.containerGroupsGeojson.features[0]) {
        coordinates = [
          parseFloat(this.containerGroupsGeojson.features[0].geometry.coordinates[0]),
          parseFloat(this.containerGroupsGeojson.features[0].geometry.coordinates[1]),
        ]
      } else {
        coordinates = [9.5382127, 48.797201]
      }

      this.map = mapUtils.initMap({
        center: {
          lng: coordinates[0],
          lat: coordinates[1],
        },
        enableFullscreen: true,
        enableNavigationControls: true,
        zoom: 9,
      })

      this.map.on('load', () => {
        this.map.loadImage(mapIcon, (error, image) => {
          if (error) throw error
          this.map.addImage('custom-marker', image)
          this.map.addSource('container-groups', {
            type: 'geojson',
            data: this.containerGroupsGeojson,
          })
          this.map.addLayer({
            id: 'container-groups',
            type: 'symbol',
            source: 'container-groups',
            layout: {
              'icon-image': 'custom-marker',
              'icon-size': 0.25,
              'icon-allow-overlap': true,
            },
          })
        })
      })
      this.map.on('click', event => {
        const features = this.map.queryRenderedFeatures(event.point, {
          layers: ['container-groups'],
        })
        if (!features.length) {
          return
        }
        const feature = features[0]
        this.$router.push(`/container_groups/${feature.properties.uuid}`)
      })
      this.map.on('mouseenter', 'container-groups', () => {
        this.map.getCanvas().style.cursor = 'pointer'
      })
      this.map.on('mouseleave', 'container-groups', () => {
        this.map.getCanvas().style.cursor = 'default'
      })
    },
    jumpOnMap(lat, lng) {
      this.map.flyTo({
        center: [lng, lat],
        essential: true,
        zoom: config.MAP_DEFAULT_ZOOM,
      })
    },
    async loadData(page) {
      page = page || this.pagination.page

      if (this.projectUuids == null) return

      this.getContainerGroups(page)

      this.containerGroupsGeojson = await containerGroupsApi.getAllGeoJson({
        company_uuid: this.companyUser.company_uuid,
        project_uuids: this.projectUuids,
        status: this.filter,
      })
      if (this.cookiesAccepted) {
        setTimeout(() => {
          this.initMap()
        }, 500)
      }
    },
    acceptCookies() {
      this.cookiesAccepted = true
      setTimeout(() => {
        this.initMap()
      }, 200)
    },
    fillLevelFormat(fraction, level) {
      const frText = fraction.substring(0, 3).toUpperCase()
      return `\u{02205} ${frText}: ${(parseFloat(level) * 100).toFixed()}%`
    },
  },
}
</script>
